import { createUploadLink } from 'apollo-upload-client';
import { ApolloClient, InMemoryCache, from, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { tryRefreshToken, handleUnauthorizedWithRefresh } from '../auth-refresh';

if (import.meta.env.NODE_ENV !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

function is401(error: any): boolean {
  const code =
    error?.statusCode || error?.status || error?.response?.status;
  return code === 401;
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isNetwork401 = networkError && is401(networkError);
  const isGql401 = graphQLErrors?.some(
    ({ extensions }) => is401(extensions) || is401((extensions as any)?.response),
  );

  if (isNetwork401 || isGql401) {
    return new Observable((observer) => {
      tryRefreshToken().then((refreshed) => {
        if (!refreshed) {
          handleUnauthorizedWithRefresh();
          observer.error(networkError || graphQLErrors?.[0]);
          return;
        }
        const token = localStorage.getItem('_authing_token');
        operation.setContext(({ headers = {} }: Record<string, any>) => ({
          headers: { ...headers, authorization: token ? `Bearer ${token}` : '' },
        }));
        forward(operation).subscribe(observer);
      });
    });
  }
});

const httpLink = createUploadLink({
  uri: '/graphql',
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('_authing_token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const GraphQLClient = new ApolloClient({
  ssrMode: typeof window === 'undefined',
  cache: new InMemoryCache({
    typePolicies: {
      Submission: {
        keyFields: ['submissionId'],
      },
      Artwork: {
        keyFields: ['artworkId'],
        fields: {
          medias: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
      Course: {
        keyFields: ['courseId'],
      },
      Assignment: {
        keyFields: ['assignmentId'],
      },
      Exhibition: {
        keyFields: ['exhibitionId'],
      },
      Lesson: {
        keyFields: ['lessonId'],
      },
      Media: {
        keyFields: ['mediaId'],
      },
      User: {
        keyFields: ['userId'],
      },
      Rubric: {
        keyFields: ['rubricId'],
      },
      RubricLevel: {
        keyFields: ['rubricLevelId'],
      },
    },
  }),
  link: from([errorLink, authLink, httpLink]),
});

export default GraphQLClient;
